import { useState } from 'react';
import { api, type PlanKind } from '../../lib/api';
import { ActionButton, Select, Modal } from '../ui';

/** Minimal plan shape the edit/clone modals need. */
export type PlanLike = { name: string; status: string; horizon_start: string; horizon_end: string; notes?: string | null; kind: PlanKind } | undefined;

const inputStyle: React.CSSProperties = {
  fontSize: 11, padding: '3px 6px', border: '0.5px solid var(--color-border-strong)',
  borderRadius: 'var(--radius-sm)', background: 'var(--color-surface)', color: 'var(--color-text)',
};

export function EditPlanModal({ planId, plan, onClose, onSaved }: { planId: string; plan: PlanLike; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(plan?.name ?? '');
  const [notes, setNotes] = useState(plan?.notes ?? '');
  const [start, setStart] = useState(plan?.horizon_start ?? '');
  const [end, setEnd] = useState(plan?.horizon_end ?? '');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const isDraft = plan?.status === 'draft';
  async function save() {
    setErr(null); setBusy(true);
    try {
      await api.fpa.plans.updateMeta(planId, {
        name, notes, ...(isDraft ? { horizon_start: start, horizon_end: end } : {}),
      });
      onSaved();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }
  return (
    <Modal title="Edit plan" onClose={onClose} width={460}>
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10, fontSize: 12 }}>
        <label>Name<input value={name} onChange={(e) => setName(e.target.value)} style={{ ...inputStyle, width: '100%', marginTop: 4 }} /></label>
        <label>Notes<textarea value={notes ?? ''} onChange={(e) => setNotes(e.target.value)} rows={3} style={{ ...inputStyle, width: '100%', marginTop: 4, resize: 'vertical' }} /></label>
        <div style={{ display: 'flex', gap: 10 }}>
          <label style={{ flex: 1 }}>Horizon start<input type="date" value={start} disabled={!isDraft} onChange={(e) => setStart(e.target.value)} style={{ ...inputStyle, width: '100%', marginTop: 4, opacity: isDraft ? 1 : 0.6 }} /></label>
          <label style={{ flex: 1 }}>Horizon end<input type="date" value={end} disabled={!isDraft} onChange={(e) => setEnd(e.target.value)} style={{ ...inputStyle, width: '100%', marginTop: 4, opacity: isDraft ? 1 : 0.6 }} /></label>
        </div>
        {!isDraft && <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Horizon can only change while the plan is in draft.</span>}
        {err && <span style={{ fontSize: 11, color: 'var(--color-red-text)' }}>{err}</span>}
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
          <ActionButton onClick={onClose}>Cancel</ActionButton>
          <ActionButton variant="primary" disabled={busy || !name.trim()} onClick={save}>{busy ? 'Saving…' : 'Save'}</ActionButton>
        </div>
      </div>
    </Modal>
  );
}

export function ClonePlanModal({ planId, plan, onClose, onCloned }: { planId: string; plan: PlanLike; onClose: () => void; onCloned: (id: string) => void }) {
  const [name, setName] = useState(plan ? `${plan.name} (copy)` : '');
  const [kind, setKind] = useState<PlanKind>(plan?.kind ?? 'forecast');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function clone() {
    setErr(null); setBusy(true);
    try {
      const r = await api.fpa.plans.clone(planId, { name, kind });
      onCloned(r.plan.id);
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }
  return (
    <Modal title="Clone plan" onClose={onClose} width={440}>
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10, fontSize: 12 }}>
        <span style={{ color: 'var(--color-text-muted)' }}>Creates a new draft copying assumptions, headcount, contracts, comp events, formulas and items. Computed lines aren't copied — recompute the new plan.</span>
        <label>Name<input value={name} onChange={(e) => setName(e.target.value)} style={{ ...inputStyle, width: '100%', marginTop: 4 }} /></label>
        <label>Kind<Select value={kind} onChange={(v) => setKind(v as PlanKind)}><option value="budget">budget</option><option value="forecast">forecast</option><option value="goal">goal</option></Select></label>
        {err && <span style={{ fontSize: 11, color: 'var(--color-red-text)' }}>{err}</span>}
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
          <ActionButton onClick={onClose}>Cancel</ActionButton>
          <ActionButton variant="primary" disabled={busy || !name.trim()} onClick={clone}>{busy ? 'Cloning…' : 'Clone'}</ActionButton>
        </div>
      </div>
    </Modal>
  );
}
